import { parse } from "date-fns";
import * as DateIso from "@tiicker/util/lib/date-iso";
import { GraphqlBundle } from "$gql/core";
import { QueryHookOptions } from "react-apollo";
import { GetIexHistoryOneDay } from "$gql/queries/historyPricing/GetIexHistoryOneDay.gen";
import { GetIexHistoryOneWeek } from "$gql/queries/historyPricing/GetIexHistoryOneWeek.gen";
import { GetIexHistoryOneMonth } from "$gql/queries/historyPricing/GetIexHistoryOneMonth.gen";
import { GetIexHistoryThreeMonth } from "$gql/queries/historyPricing/GetIexHistoryThreeMonth.gen";
import { GetIexHistoryOneYear } from "$gql/queries/historyPricing/GetIexHistoryOneYear.gen";
import { GetIexHistoryFiveYear } from "$gql/queries/historyPricing/GetIexHistoryFiveYear.gen";

export interface IexHistoricalSegment {
  __typename?: string | undefined;
  isoDate?: DateIso.Type | null;
  date?: string | null;
  close?: number | null;
  volume?: number | null;

  minute?: string | null;
  label?: string | null;
  high?: number | null;
  low?: number | null;
  open?: number | null;
  average?: number | null;
  notional?: number | null;
  numberOfTrades?: number;

  marketOpen?: number;
  marketHigh?: number;
  marketLow?: number;
  marketClose?: number;
  marketAverage?: number;
  marketVolume?: number;
  marketNotional?: number;
  marketNumberOfTrades?: number;

  uOpen?: number;
  uClose?: number;
  uHigh?: number;
  uLow?: number;
  uVolume?: number;
  change?: number;
  changePercent?: number;
  changeOverTime?: number;
}
export type TimeSelection = "1d" | "1w" | "1m" | "3m" | "1y" | "5y";

interface DateMapping {
  [key: number]: Date;
}

interface ParseDataPoint {
  date: Date | null;
  y: number | null;
  x: number;
}

export interface ParsedDataSpan {
  data: {
    id: string;
    data: ParseDataPoint[];
  }[];
  starting: number;
  ending: number;
  max: number;
  min: number;
  dateBuckets: DateMapping;
}

type ConfigType<Result, Vars> = {
  query: GraphqlBundle<Result, Vars>;
  options: QueryHookOptions<Result, Vars>;
  resultFetch(result: Result): ParsedDataSpan;
  formatString: string;

  // If each time interval needs specific configuration
  // this is how to do it.
  // chartOptions?(
  //   indexToDateMapping: DateMapping,
  //   formatString: string
  // ): Pick<LineSvgProps, "axisBottom">;
};

export type ConfigListType = {
  [key in TimeSelection]: ConfigType<any, any>;
};

function setupConfig<Result, Vars>(
  config: ConfigType<Result, Vars>
): ConfigType<Result, Vars> {
  return config;
}

const convertDate = (date, time) => {
  return parse(`${date} ${time || "00:00"}`, "yyyy-MM-dd HH:mm", new Date());
};

/**
 * This function parses through incoming data and gathers it
 * in the particular shape we need it for the graph.
 *
 * @param incomingData The data coming from the IEX API
 * @param valueToUse The value to use as the point in processing the IEX data
 * @param indexToFillTo (Optional) Set a number of segments that need to appear for this data
 */
export function parseData(
  incomingData: IexHistoricalSegment[],
  valueToUse: "close" | "average",
  indexToFillTo?: number
): ParsedDataSpan {
  const actualData = incomingData.filter((s) => !!s[valueToUse]);

  if (actualData.length === 0) {
    return {
      data: [],
      starting: 0,
      ending: 0,
      max: 0,
      min: 0,
      dateBuckets: {},
    };
  }

  // Determine first and last values in the set
  const starting = actualData[0][valueToUse] || 0;
  const ending = actualData[actualData.length - 1][valueToUse] || 0;

  // Setup variables to keep track of statistics during the map loop
  let max = starting;
  let min = starting;
  let dateBuckets = {};

  const values: ParseDataPoint[] = actualData

    // Filter out any values that don't have a value
    // NOTE: Sandbox data DOES have null values scattered throughout
    // This will fitler them out and reduce the general viability
    // of the chart
    .filter((segment) => !!segment[valueToUse])
    .map((segment, i) => {
      // This value can't be undefined because of the check above,
      // this is to appease typechecking.
      const segmentValue = segment[valueToUse] || 0;

      if (segmentValue !== undefined) {
        // Evaluate max and min while iterating
        max = segmentValue > max ? segmentValue : max;
        min = segmentValue < min ? segmentValue : min;
      }

      const parsedDate = convertDate(
        segment.date || segment.isoDate,
        segment.minute
      );

      // Keep track of which index corresponds to which date
      // if we ever need to customize views based on this
      dateBuckets[i] = parsedDate;

      return {
        date: parsedDate,
        y: segmentValue,
        x: i,
      };
    });

  // In order to display a full graph, this may be used to add a blank spacing
  // within the graph. EG: Daily graph has a known segment length of 84, and
  // it's early in the morning with only 5 segments. We need to fill the remaining
  // graph with empty slots. This is where that happens.
  const fillValueArray =
    indexToFillTo !== undefined
      ? Array.from(new Array(indexToFillTo - values.length).keys())
      : [];

  // Fill the values
  const fillValuesWtihIndex: ParseDataPoint[] = fillValueArray.map((value) => {
    return { date: null, y: null, x: value + values.length };
  });

  // Concat fill values to current values
  const finalValues = values.concat(fillValuesWtihIndex);

  return {
    data: [{ id: "Stock Data", data: finalValues }],
    starting,
    ending,
    max,
    min,
    dateBuckets,
  };
}

// This is where all of the display configuration for each segment is defined.
// If any other chart options need to be provided here, add onto `ConfigType`
// where the return type of the `chartOptions` function is defined
//
// Also note that `chartOptions` is passed a mapping. This is provided in
// the `parseData` function, and is required due to skipping time intervals over
// the weekend. The native date parsing within the library doesn't suffice, so
// we need to manually map them
//
// This also provides all graphql mapping to the time segment
const createLineGraphConfig = (tickerSymbol: string): ConfigListType => {
  const globalOptions = { variables: { ticker: tickerSymbol } };
  return {
    "1d": setupConfig({
      query: GetIexHistoryOneDay,
      options: globalOptions,
      formatString: "h:mm a",
      resultFetch: (result) => {
        // NOTE: This 84 value comes from assuming the API is sending over data
        // every 5 minutes. 84 = 7 (hours of trading) * 12 (segments per hour)
        return parseData(result.iex.history?.h1d?.data || [], "average", 78);
      },
    }),
    "1w": setupConfig({
      query: GetIexHistoryOneWeek,
      options: globalOptions,
      formatString: "E LLL d",
      resultFetch: (result) =>
        parseData(result.iex.history?.h1w?.data || [], "close"),
    }),
    "1m": setupConfig({
      query: GetIexHistoryOneMonth,
      options: globalOptions,
      formatString: "LLL d",
      resultFetch: (result) =>
        parseData(result.iex.history?.h1m?.data || [], "close"),
    }),
    "3m": setupConfig({
      query: GetIexHistoryThreeMonth,
      options: globalOptions,
      formatString: "LLL d yyy",
      resultFetch: (result) =>
        parseData(result.iex.history?.h3m?.data || [], "close"),
    }),
    "1y": setupConfig({
      query: GetIexHistoryOneYear,
      options: globalOptions,
      formatString: "LLL d yyy",
      resultFetch: (result) =>
        parseData(result.iex.history?.h1y?.data || [], "close"),
    }),
    "5y": setupConfig({
      query: GetIexHistoryFiveYear,
      options: globalOptions,
      formatString: "LLL d yyy",
      resultFetch: (result) =>
        parseData(result.iex.history?.h5y?.data || [], "close"),
    }),
  };
};

export { createLineGraphConfig };
